import type { MyMoments } from '../../api/moments';
import { formatPeriodRange } from '../../utils/calendarDate';
import type { Moment } from './moments';
import i18n from '../../i18n';

/** "September" / "septiembre" in the UI's language — read on the calendar day stored (UTC), never shifted by a zone. */
function monthName(iso: string): string {
  return new Intl.DateTimeFormat(i18n.language, { month: 'long', timeZone: 'UTC' }).format(new Date(iso));
}

/**
 * The moment to show now, from the server's facts — or null. PURE.
 *
 * - A trimester closed for a client comes first: it is the larger accomplishment, and the one with
 *   confetti. Its closing review's own "review complete" fact is marked seen with it, so one act is
 *   celebrated once.
 * - Otherwise the newest unseen completed review, quietly. Older unseen completions are marked seen
 *   with it, so ten reviews completed in one sitting produce one moment, not ten across ten visits.
 * - Only facts the server listed, and only figures it proved: the client name and the period.
 */
export function pickMoment(data: MyMoments | undefined): { moment: Moment; alsoSeen: string[] } | null {
  if (!data || !data.available || !data.enabled) return null;
  const seen = new Set(data.seen);

  const closed = data.trimestersClosed.find((t) => !seen.has(t.id));
  if (closed) {
    const closingFact = data.reviewsCompleted.find((r) => r.reviewId === closed.closedByReviewId);
    return {
      moment: {
        id: closed.id,
        kind: 'trimester-closed',
        title: i18n.t('moments.trimesterClosed.title', { trimester: closed.trimester, year: closed.year, client: closed.clientName }),
        detail: i18n.t('moments.trimesterClosed.detail', { from: monthName(closed.start), to: monthName(closed.end) }),
        confetti: true,
      },
      alsoSeen: closingFact && !seen.has(closingFact.id) ? [closingFact.id] : [],
    };
  }

  const unseen = data.reviewsCompleted.filter((r) => !seen.has(r.id));
  if (unseen.length === 0) return null;
  const [newest, ...older] = [...unseen].sort((a, b) => b.completedAt.localeCompare(a.completedAt));
  return {
    moment: {
      id: newest.id,
      kind: 'review-completed',
      title: i18n.t('moments.reviewCompleted.title'),
      detail: `${newest.clientName} · ${formatPeriodRange(newest.periodStart, newest.periodEnd)}`,
      confetti: false,
    },
    alsoSeen: older.map((r) => r.id),
  };
}
